import React from "react";
import { apiDirectCall } from "customHooks/useApi/api";
import { useAuthState } from "_ReactContext/AuthContext";
import { useTranslation } from "react-i18next";

// Types matching the backend response
export interface NLSearchResponse {
    success: boolean;
    filters: Compleo.IObject;
    interpretation: string;
    confidence: number;
    mergeStrategy: "replace" | "merge";
    error?: string;
}

export interface NLSearchResult {
    data: NLSearchResponse | null;
    loading: boolean;
    error: string | null;
}

export interface UseNaturalLanguageSearchOptions {
    moduleName: string;
    currentAggregations?: Compleo.IObject;
    onSuccess?: (result: NLSearchResponse) => void;
    onError?: (error: string) => void;
}

/**
 * Custom hook for natural language search that converts text queries to filters
 *
 * @example
 * ```tsx
 * const { search, result, isLoading } = useNaturalLanguageSearch({
 *     moduleName: "APPLICANTLIST",
 *     onSuccess: (result) => {
 *         setFilters(prev => mergeFilters(prev, result.filters, result.mergeStrategy));
 *     }
 * });
 *
 * // In your component:
 * <input
 *     value={query}
 *     onChange={(e) => setQuery(e.target.value)}
 *     onKeyDown={(e) => e.key === 'Enter' && search(query)}
 * />
 * <button onClick={() => search(query)}>Search</button>
 * ```
 */
export const useNaturalLanguageSearch = (
    options: UseNaturalLanguageSearchOptions
) => {
    const { moduleName, currentAggregations, onSuccess, onError } = options;
    const { i18n } = useTranslation();
    const { company } = useAuthState();

    const [result, setResult] = React.useState<NLSearchResult>({
        data: null,
        loading: false,
        error: null
    });

    const search = React.useCallback(
        async (query: string) => {
            if (!query || query.trim().length === 0) {
                setResult({
                    data: null,
                    loading: false,
                    error: "Query cannot be empty"
                });
                return null;
            }

            setResult((prev) => ({ ...prev, loading: true, error: null }));

            try {
                const response = await apiDirectCall(
                    "/ai/naturallanguagesearch",
                    "post",
                    {
                        companyId: company.companyId,
                        query: query.trim(),
                        moduleName,
                        language: i18n.language || "pt-BR",
                        currentAggregations
                    }
                );

                // The API wraps response in retornoUtil.success({ data: result })
                // So we get: response.data = { statusCode, body: { data: NLSearchResponse } }
                const responseBody = response.data?.body || response.data;
                const nlData = responseBody?.data;

                // Check if we have valid filter data (even if success is false, we may have partial results)
                const hasValidFilters =
                    nlData?.filters &&
                    (nlData.filters.mainSearch ||
                        nlData.filters.customSearch ||
                        nlData.filters.buckets ||
                        Object.keys(nlData.filters).length > 0);

                if (nlData?.success || hasValidFilters) {
                    // Use the data even if success is false - partial results are still useful
                    const nlResponse = nlData as NLSearchResponse;

                    setResult({
                        data: nlResponse,
                        loading: false,
                        error: nlData?.success
                            ? null
                            : nlData?.interpretation || null
                    });

                    if (onSuccess) {
                        onSuccess(nlResponse);
                    }

                    return nlResponse;
                } else {
                    const errorMessage =
                        nlData?.error ||
                        responseBody?.message ||
                        "Não foi possível interpretar a busca";
                    setResult({
                        data: null,
                        loading: false,
                        error: errorMessage
                    });

                    if (onError) {
                        onError(errorMessage);
                    }

                    return null;
                }
            } catch (err) {
                const errorMessage =
                    err instanceof Error ? err.message : "Request failed";

                setResult({
                    data: null,
                    loading: false,
                    error: errorMessage
                });

                if (onError) {
                    onError(errorMessage);
                }

                return null;
            }
        },
        [
            moduleName,
            company.companyId,
            currentAggregations,
            i18n.language,
            onSuccess,
            onError
        ]
    );

    const reset = React.useCallback(() => {
        setResult({
            data: null,
            loading: false,
            error: null
        });
    }, []);

    return {
        search,
        reset,
        result: result.data,
        isLoading: result.loading,
        error: result.error,
        interpretation: result.data?.interpretation || null,
        confidence: result.data?.confidence || 0,
        mergeStrategy: result.data?.mergeStrategy || "replace"
    };
};

/**
 * Merges NL search filters with existing filters based on merge strategy
 */
export const mergeNLFilters = (
    currentFilters: Compleo.IObject,
    nlFilters: Compleo.IObject,
    strategy: "replace" | "merge"
): Compleo.IObject => {
    if (strategy === "replace") {
        // Replace: clear existing and apply new
        return {
            ...currentFilters,
            mainSearch: nlFilters.mainSearch || "",
            customSearch: nlFilters.customSearch || {},
            buckets: nlFilters.buckets || {},
            sort: nlFilters.sort || currentFilters.sort,
            filterUpdated: true,
            pagination: {
                ...currentFilters.pagination,
                currentPage: 0,
                fromValue: 0
            }
        };
    } else {
        // Merge: deep merge with existing filters
        return {
            ...currentFilters,
            mainSearch: nlFilters.mainSearch || currentFilters.mainSearch,
            customSearch: {
                ...currentFilters.customSearch,
                ...(nlFilters.customSearch || {})
            },
            buckets: mergeBuckets(
                currentFilters.buckets || {},
                nlFilters.buckets || {}
            ),
            sort: nlFilters.sort || currentFilters.sort,
            filterUpdated: true,
            pagination: {
                ...currentFilters.pagination,
                currentPage: 0,
                fromValue: 0
            }
        };
    }
};

/**
 * Merges bucket filters, adding new values to existing buckets
 */
const mergeBuckets = (
    currentBuckets: Compleo.IObject,
    newBuckets: Compleo.IObject
): Compleo.IObject => {
    const merged = { ...currentBuckets };

    for (const [bucketName, bucketData] of Object.entries(newBuckets)) {
        if (!merged[bucketName]) {
            merged[bucketName] = bucketData;
        } else {
            // Merge values arrays, avoiding duplicates
            const existingValues = merged[bucketName].values || [];
            const newValues = (bucketData as any).values || [];

            const existingKeys = new Set(
                existingValues.map((v: any) => v.value)
            );
            const uniqueNewValues = newValues.filter(
                (v: any) => !existingKeys.has(v.value)
            );

            merged[bucketName] = {
                ...merged[bucketName],
                values: [...existingValues, ...uniqueNewValues]
            };
        }
    }

    return merged;
};

export default useNaturalLanguageSearch;
